import { CompanyProfileSearchParams } from '../components/misc/CompanyProfile/CompanyProfile'
import { GetPanelRequestsParams } from '../hooks/usePanelRequestsQuery'
import { IntegrationService } from '../types/misc'
import {
  GetClientsParams,
  GetCompassClientsParams,
} from '../types/requests/clients'
import {
  GetReferralFirmIndividualsParams,
  GetReferralFirmsQuery,
  GetShareableRoundRobinReferralLinksParams,
} from '../types/requests/companies'
import {
  GetCompanyIntegrationLogParams,
  GetCompanyIntegrationStatsParams,
  GetIntellifloAdvisersParams,
  GetPlannrAdvisersParams,
} from '../types/requests/company-integrations'
import {
  GetCompanyUsersParams,
  GetShareableIndividualReferralLinksParams,
} from '../types/requests/company-users'
import {
  GetCompassReportsWithActionsParams,
  GetCompassReportsWithBusinessSaleParams,
  GetCompassReportsWithMortgageParams,
  GetCompassReportsWithPensionDetailsParams,
  GetCompassReportsWithProtectionDetailsParams,
  GetCompassReportsWithWillDetailsParams,
} from '../types/requests/compass-reports'
import { GetGroupUserInvitesParams } from '../types/requests/invite-group-users'
import { GetUserInvitesParams } from '../types/requests/invite-users'
import { GetMeetingLocationsParams } from '../types/requests/meeting-locations'
import { GetPanelsWithLeadAdvisersParams } from '../types/requests/panels'
import { GetReferralLinkClientFieldsParams } from '../types/requests/referral-link-client-fields'
import { GetReferralLinksParams } from '../types/requests/referral-links'
import { GetReferralDashboardStatsRequest } from '../types/requests/referral-stats'
import {
  GetReferralIndividualsRequestParams,
  GetReferralInvitesRequestParams,
  GetReferralServiceAreaRequestParams,
  ReferralCollectionParams,
} from '../types/requests/referrals'
import {
  GetPanelPartnersParams,
  GetPanelsParams,
} from './services/panelService'

export const QUERY_KEYS = {
  addresses: () => ['addresses'],
  appState: ['app-state'],
  approvalRequests: ['approval-requests'],
  availableRecommendedPartners: () => ['available-recommended-partners'],
  clients: (params?: GetClientsParams) =>
    params ? ['clients', params] : ['clients'],
  client: (id: number) => ['client', { id }],
  commercialAgreements: () => ['commercial-agreements'],
  commercialAgreement: (id: number) => ['commercial-agreements', { id }],
  companyBankDetails: ['company-bank-details'],
  companyByReferralCode: (referralCode: string) => [
    'company-by-referral-code',
    { referralCode },
  ],
  companyCompassConfig: ['company-compass-config'],
  companyCompassSnapshots: (params?: GetCompassClientsParams) =>
    params
      ? ['company-compass-snapshots', params]
      : ['company-compass-snapshots'],
  companyDetails: (companyId: number) => ['company-details', { companyId }],
  companyNpsFeedback: (companyId: number) => [
    'company-nps-feedback',
    { companyId },
  ],
  companyReferralFirms: (params?: GetReferralFirmsQuery) =>
    params ? ['company-referral-firms', params] : ['company-referral-firms'],
  companyServices: (companyId: number) => ['company-services', { companyId }],
  compassPartners: (params?: { companyId: number }) =>
    params ? ['compass-partners', params] : ['compass-partners'],
  compassReportSharings: ['compass-report-sharings'],
  compassReportsWithActions: (params: GetCompassReportsWithActionsParams) =>
    params
      ? ['compass-reports-with-actions', params]
      : ['compass-reports-with-actions'],
  compassReportsWithBusinessSale: (
    params: GetCompassReportsWithBusinessSaleParams,
  ) =>
    params
      ? ['compass-reports-with-business-sale', params]
      : ['compass-reports-with-business-sale'],
  compassReportsWithMortgage: (params: GetCompassReportsWithMortgageParams) =>
    params
      ? ['compass-reports-with-mortgage', params]
      : ['compass-reports-with-mortgage'],
  compassReportsWithWillDetails: (
    params: GetCompassReportsWithWillDetailsParams,
  ) =>
    params
      ? ['compass-reports-with-will-details', params]
      : ['compass-reports-with-will-details'],
  compassReportsWithProtectionDetails: (
    params: GetCompassReportsWithProtectionDetailsParams,
  ) =>
    params
      ? ['compass-reports-with-protection-details', params]
      : ['compass-reports-with-protection-details'],
  compassReportsWithPensionDetails: (
    params: GetCompassReportsWithPensionDetailsParams,
  ) =>
    params
      ? ['compass-reports-with-pension-details', params]
      : ['compass-reports-with-pension-details'],
  compassReport: (token?: string) =>
    token ? ['compass-report', { token }] : ['compass-report'],
  compassSnapshot: (token?: string) =>
    token ? ['compass-snapshot', { token }] : ['compass-snapshot'],
  companiesHouseSearch: ({ name }: { name: string }) => [
    'companies-house-search',
    { name },
  ],
  company: (id: number) => ['company', { id }],
  companyNylasIntegration: ['company-nylas-integration'],
  companyIntegrations: () => ['company-integrations'],
  companyIntegrationByType: (type: IntegrationService) => [
    'company-integrations',
    { type },
  ],
  companyIntegrationLogs: (params?: GetCompanyIntegrationLogParams) =>
    params
      ? ['company-integration-logs', params]
      : ['company-integration-logs'],
  companyIntegrationStats: (params?: GetCompanyIntegrationStatsParams) =>
    params
      ? ['company-integration-stats', params]
      : ['company-integration-stats'],

  companyProfile: (
    params?: Pick<CompanyProfileSearchParams, 'identifierType' | 'identifier'>,
  ) => (params ? ['company-profile', params] : ['company-profile']),
  companyEmails: {
    all: ['company-emails'],
    compassInviteEmail: () => [
      QUERY_KEYS.companyEmails.all,
      { emailType: 'COMPASS_INVITE' },
    ],
  },
  companySmtpDetail: (companyId: number) => [
    'company-smtp-detail',
    { companyId },
  ],
  companyUsers: (params?: GetCompanyUsersParams) =>
    params ? ['company-users', params] : ['company-users'],
  companyUserIntegrationByType: (type: IntegrationService) => [
    'company-user-integration-by-type',
    type,
  ],
  companyCompanyUserIntegrationsByType: (type: IntegrationService) => [
    'company-company-user-integrations-by-type',
    type,
  ],
  directorySearch: ({ name }: { name: string }) => [
    'directory-search',
    { name },
  ],
  fcaServiceTypes: ['fca-service-types'],
  intellifloAdvisers: (params?: GetIntellifloAdvisersParams) =>
    params ? ['intelliflo-advisers', params] : ['intelliflo-advisers'],
  intellifloCampaigns: () => ['intelliflo-campaigns'],
  intellifloHealthCheck: () => ['intelliflo-health-check'],
  intellifloMetadata: () => ['intelliflo-metadata'],
  intellifloStatuses: (companyIntegrationId: number) => [
    'intelliflo-statuses',
    { companyIntegrationId },
  ],
  inviteUser: (inviteId: number, inviteCode: string) => [
    'invite-user',
    { inviteId, inviteCode },
  ],
  inviteGroupUser: (inviteId: number, inviteCode: string) => [
    'invite-group-user',
    { inviteId, inviteCode },
  ],
  isOnline: (navigatorOnlineProp: boolean) => [
    'is-online',
    { navigatorOnlineProp },
  ],
  latestCompassReport: ['latest-compass-report'],
  latestCompletedCompassReport: (clientId: number) => [
    'latest-completed-compass-report',
    { clientId },
  ],
  mailingLists: (companyUserId: number) => ['mailing-lists', { companyUserId }],
  meetingLocations: (params?: GetMeetingLocationsParams) =>
    params ? ['meeting-locations', params] : ['meeting-locations'],
  nylasCalendarIntegration: ['nylas-calendar-integration'],
  nylasEmailIntegration: ['nylas-email-integration'],
  networkOnboarding: ['network-onboarding'],
  onboardingChecklist: () => ['onboarding-checklist'],
  panels: (params?: GetPanelsParams) =>
    params ? ['panels', params] : ['panels'],
  panelsWithLeadAdvisers: (params?: GetPanelsWithLeadAdvisersParams) => [
    params
      ? ['panels-with-lead-advisers', params]
      : ['panels-with-lead-advisers'],
  ],
  panel: (panelId?: number) => (panelId ? ['panels', { panelId }] : ['panels']),
  panelPartners: (params?: GetPanelPartnersParams) =>
    params ? ['panel-partners', params] : ['panel-partners'],
  panelApprovals: (fromCompanyId: number) => [
    'panel-approvals',
    { fromCompanyId },
  ],
  panelRequests: (params?: GetPanelRequestsParams) =>
    params ? ['panel-requests', params] : ['panel-requests'],
  panelAgreements: (panelId?: number) =>
    panelId ? ['panel-agreements', { panelId }] : ['panel-agreements'],
  panelStats: () => ['panel-stats'],
  plannrAuthInit: () => ['plannr-auth-init'],
  plannrAdvisers: (params?: GetPlannrAdvisersParams) =>
    params ? ['plannr-advisers', params] : ['plannr-advisers'],
  plannrCampaigns: () => ['plannr-campaigns'],
  plannrIntroducers: () => ['plannr-introducers'],
  plannrHealthCheck: () => ['plannr-health-check'],
  plannrMetadata: () => ['plannr-metadata'],
  plannrStatuses: (companyIntegrationId: number) => [
    'plannr-statuses',
    { companyIntegrationId },
  ],
  recommendedPartners: () => ['recommended-partners'],
  recommendedPartner: (companyId: number) => [
    'recommended-partners',
    { companyId },
  ],
  referClient: {
    all: ['refer-client'],
    referralFirm: (id: number) => ['referral-firm', { id }],
  },
  referralCompanyTypes: () => ['referral-company-types'],
  referralFirmIndividuals: (
    params?: GetReferralFirmIndividualsParams & { referralFirmId: number },
  ) =>
    params
      ? ['referral-firm-individuals', params]
      : ['referral-firm-individuals'],
  referralLinkClientFields: (params?: GetReferralLinkClientFieldsParams) =>
    params
      ? ['referral-link-client-fields', params]
      : ['referral-link-client-fields'],
  referralServices: (params?: GetReferralServiceAreaRequestParams) =>
    params ? ['referral-services', params] : ['referral-services'],
  referralSetupData: () => ['referral-setup-data'],
  referralWithESignatureDetails: (id: number) => [
    'referral-esignature',
    { id },
  ],
  rqCertifiedAssessment: ['rq-certified-assessment'],
  referralCommissionConsent: (id: number) => [
    'request-commission-consent',
    { id },
  ],
  referralFeedback: (id: number) => ['referral-feedback', { id }],
  referralIndividuals: (params: GetReferralIndividualsRequestParams) =>
    params ? ['referral-individuals', params] : ['referral-individuals'],
  referralInvite: (id: number) => ['referral-invite', { id }],
  referralItem: (id: number) => ['referral-item', { id }],
  referrals: (params?: ReferralCollectionParams) =>
    params ? ['referrals', params] : ['referrals'],
  referralDashboardStats: (params: GetReferralDashboardStatsRequest) =>
    params
      ? ['referral-dashboard-stats', params]
      : ['referral-dashboard-stats'],
  referralInvites: (params: GetReferralInvitesRequestParams) => [
    'referral-invites',
    params,
  ],
  referralLinks: (params?: GetReferralLinksParams) =>
    params ? ['referral-links', params] : ['referral-links'],
  referralLinkByCode: (code: string) => ['referral-link-by-code', { code }],
  recentReferrals: () => ['recentReferrals'],
  referralNotes: (id: number) => ['referral-notes', { id }] as const,
  referralNoteMessages: (id: number) =>
    ['referral-note-messages', { id }] as const,
  referralTargets: (searchQuery: string) => [
    'referralTargets',
    { searchQuery },
  ],
  requestCallback: (referralId: number) => ['request-callback', { referralId }],
  reviews: (companyId: number) => ['reviews', { companyId }],
  roundRobinAdministrator: (companyId: number) => [
    'roundRobinAdministrator',
    { companyId },
  ],
  services: (companyId: number) => ['services', { companyId }],
  shareableIndividualReferralLinks: (
    params?: GetShareableIndividualReferralLinksParams,
  ) =>
    params
      ? ['shareable-individual-referral-links', params]
      : ['shareable-individual-referral-links'],
  shareableRoundRobinReferralLinks: (
    params?: GetShareableRoundRobinReferralLinksParams,
  ) =>
    params
      ? ['shareable-round-robin-referral-links', params]
      : ['shareable-round-robin-referral-links'],
  sraSearch: ({ name }: { name: string }) => ['sra-search', { name }],
  subscriptions: (companyId: number, companyUserId: number) => [
    'subscription',
    { companyId, companyUserId },
  ],
  userGroupInvites: (params?: GetGroupUserInvitesParams) =>
    params ? ['user-group-invites', params] : ['user-group-invites'],
  userInvites: (params?: GetUserInvitesParams) =>
    params ? ['user-invites', params] : ['user-invites'],
  companyUserProvisioning: () => ['company-user-provisioning'],
  invitations: (userId: number) => ['invitations', { userId }],
  companyUserProvisionByCode: (id: number) => [
    'company-user-provision-by-code',
    { id },
  ],
}
